import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/ui/Icon';
import { errorMessage } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import type { AttachmentRef, OutboxAttachment } from '@shared/mail';
import { removeUpload, uploadFile } from './data';
import { fileBadge, fitFiles, totalBytes, type DraftAttachment } from './logic';

// Files on an email: uploaded to the server as soon as they are added, and kept there until the email is sent.

export interface AttachItem {
  key: string;
  name: string;
  size: number;
  mime: string;
  /** Set once the file is on the server. */
  ref: AttachmentRef | null;
  /** Upload progress, 0 to 1. */
  progress: number;
  error: string | null;
}

let nextKey = 1;

export function fromSaved(list: (OutboxAttachment | DraftAttachment)[]): AttachItem[] {
  return list.map((a) => ({
    key: `a${nextKey++}`,
    name: a.name,
    size: a.size,
    mime: a.mime,
    ref: a.kind === 'upload' ? { kind: 'upload', id: a.id! } : { kind: 'email', emailId: a.emailId!, attachmentId: a.attachmentId! },
    progress: 1,
    error: null,
  }));
}

export function toSaved(items: AttachItem[]): DraftAttachment[] {
  return items.flatMap((a) => (a.ref ? [{ ...a.ref, name: a.name, size: a.size, mime: a.mime }] : []));
}

export function useAttachments(initial: AttachItem[], max: number, onChange: () => void) {
  const [items, setItems] = useState<AttachItem[]>(initial);
  const aborts = useRef(new Map<string, () => void>());
  const changed = useRef(onChange);
  changed.current = onChange;

  useEffect(() => {
    const map = aborts.current;
    return () => {
      for (const abort of map.values()) abort();
    };
  }, []);

  const patch = (key: string, p: Partial<AttachItem>) => setItems((list) => list.map((a) => (a.key === key ? { ...a, ...p } : a)));

  /** Starts uploading the files that fit. Returns the reason when some would take the email over the size limit. */
  const add = useCallback(
    (files: File[]): string | null => {
      if (!files.length) return null;
      const empty = files.find((f) => f.size === 0);
      if (empty) return `${empty.name || 'That file'} is empty.`;
      const { fit, problem } = fitFiles(
        totalBytes(items.filter((a) => !a.error)),
        files,
        max,
      );
      const added: AttachItem[] = fit.map((f) => ({
        key: `a${nextKey++}`,
        name: f.name || 'Attachment',
        size: f.size,
        mime: f.type || 'application/octet-stream',
        ref: null,
        progress: 0,
        error: null,
      }));
      setItems((list) => [...list, ...added]);
      added.forEach((item, i) => {
        const up = uploadFile(fit[i]!, (p) => patch(item.key, { progress: p }));
        aborts.current.set(item.key, up.abort);
        up.promise
          .then((f) => {
            patch(item.key, { ref: { kind: 'upload', id: f.id }, progress: 1 });
            changed.current();
          })
          .catch((err: unknown) => {
            if ((err as { code?: string }).code === 'aborted') return;
            patch(item.key, { error: errorMessage(err) });
          })
          .finally(() => aborts.current.delete(item.key));
      });
      if (added.length) changed.current();
      return problem;
    },
    [items, max],
  );

  const latest = useRef(items);
  latest.current = items;

  const remove = useCallback((key: string) => {
    const item = latest.current.find((a) => a.key === key);
    aborts.current.get(key)?.();
    if (item?.ref?.kind === 'upload') void removeUpload(item.ref.id).catch(() => undefined);
    setItems((list) => list.filter((a) => a.key !== key));
    changed.current();
  }, []);

  const uploading = items.some((a) => !a.ref && !a.error);
  const failed = items.some((a) => a.error);
  return { items, add, remove, uploading, failed, setItems };
}

export function AttachmentList({ items, onRemove }: { items: AttachItem[]; onRemove: (key: string) => void }) {
  if (!items.length) return null;
  return (
    <div className="cmp-attach">
      {items.map((a) => {
        const badge = fileBadge(a.name, a.mime);
        const pct = Math.round(a.progress * 100);
        return (
          <span key={a.key} className={['attach', a.error ? 'bad' : '', !a.ref && !a.error ? 'busy' : ''].filter(Boolean).join(' ')}>
            <span className={['fi', badge.pdf ? 'pdf' : ''].filter(Boolean).join(' ')}>{badge.label}</span>
            <span className="grow">
              <b title={a.name}>{a.name}</b>
              <span role={a.error ? 'alert' : undefined}>{a.error ? a.error : a.ref ? formatBytes(a.size) : `Uploading · ${pct}%`}</span>
              {!a.ref && !a.error ? (
                <span className="up-bar" aria-hidden="true">
                  <i style={{ width: `${pct}%` }} />
                </span>
              ) : null}
            </span>
            <button type="button" className="icon-btn xs" aria-label={`Remove ${a.name}`} data-tip={a.ref || a.error ? 'Remove' : 'Stop upload'} onClick={() => onRemove(a.key)}>
              <Icon name="x" />
            </button>
          </span>
        );
      })}
    </div>
  );
}
